import React, { useState, useEffect, useContext, useRef } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import Tooltip from '@mui/material/Tooltip';
import ThemeContext from '../lib/theme-context';
import throwMessage from '../utils/throwMessage';

const VoiceToText = ({ onTranscript, currentValue }) => {
  const theme = useContext(ThemeContext);
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [baseText, setBaseText] = useState('');
  const recognitionRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  const baseTextRef = useRef(baseText);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
    baseTextRef.current = baseText;
  });

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Update with both final and interim results for real-time display
        const combinedTranscript = finalTranscript + interimTranscript;
        
        if (combinedTranscript) {
          const currentBaseText = baseTextRef.current;
          const newText = currentBaseText && currentBaseText.trim() 
            ? currentBaseText + ' ' + combinedTranscript.trim()
            : combinedTranscript.trim();
          onTranscriptRef.current(newText);
          
          // Only update base text when we have final results
          if (finalTranscript) {
            setBaseText(newText);
          }
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        
        if (event.error === 'no-speech') {
          // No speech detected
        } else if (event.error === 'not-allowed') {
          throwMessage('error', 'Enable microphone permissions.');
        }
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognitionInstance;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleRecording = () => {
    if (!recognitionRef.current) return;

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      // Set the base text to current value when starting
      setBaseText(currentValue || '');
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <Tooltip title={isRecording ? "Recording... click to stop" : "Click to speak"}>
      <div
        onClick={toggleRecording}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: isRecording 
            ? 'rgba(242, 54, 69, 0.1)' 
            : theme[0] === 'dark' 
              ? 'rgba(156, 163, 175, 0.1)' 
              : 'rgba(107, 114, 128, 0.08)',
          border: `2px solid ${isRecording ? '#f23645' : theme[0] === 'dark' ? '#2d2e33' : '#e5e7eb'}`,
          transition: 'all 0.3s ease',
          animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          if (!isRecording) {
            e.currentTarget.style.backgroundColor = theme[0] === 'dark' 
              ? 'rgba(156, 163, 175, 0.2)' 
              : 'rgba(107, 114, 128, 0.15)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isRecording) {
            e.currentTarget.style.backgroundColor = theme[0] === 'dark' 
              ? 'rgba(156, 163, 175, 0.1)' 
              : 'rgba(107, 114, 128, 0.08)';
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        <MicIcon 
          sx={{ 
            fontSize: '20px',
            color: isRecording ? '#f23645' : theme[0] === 'dark' ? '#9ca3af' : '#6b7280',
          }} 
        />
      </div>
    </Tooltip>
  );
};

export default VoiceToText;
